import React, { useCallback, useEffect, useMemo } from 'react';
import find from 'lodash/find';
import head from 'lodash/head';
import size from 'lodash/size';
import isEqual from 'lodash/isEqual';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../../../products/productsTypes';

import {
  FETCH_PRODUCT_BRANDS_SELECTED_QUERY,
  FetchProductBrandsSelectedQueryResponse
} from '../../../../../../../../products/queries/fetchProductBrandsSelected.query';

import { useFinPaginatedCategories } from '../../../../../../../../categories/hooks/useFinPaginatedCategories';

import { Translate } from '../../../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../../../helpers/buttons/PureIconButtonHelper';

import { categoriesKeys } from '../../../../../../../../../locales/keys';

import { IconsEnum } from '../../../../../../../../../assets/icons/types';

import { CategoriesPermissions } from '../../../../../../../../categories/categoriesConstants';
import { ProductCache } from '../../../../../../../../products/ProductCache';
import { FetchCategoriesScopes } from '../../../../../../../../categories/categoriesTypes';

const staleTime = 1000 * 60 * 60;

interface ProductsBrandsFilterBadgeProps {
  changeProductsFilters: ChangeProductsFiltersFunc;
  productsFilters: FetchProductsFilters;
}

function ProductsBrandsFilterBadge({
  changeProductsFilters,
  productsFilters
}: ProductsBrandsFilterBadgeProps) {
  const brandIds = useMemo(
    () => productsFilters[FetchProductsFilterFields.BRAND_ID]?.in || [],
    [productsFilters]
  );

  const {
    categories: productBrandsSelected,
    categoriesFilters: productsBrandsSelectedFilters,
    changeCategoriesFilters: changeProductBrandsSelectedFilters
  } = useFinPaginatedCategories<FetchProductBrandsSelectedQueryResponse>({
    action: CategoriesPermissions.READ_PRODUCT_CATEGORIES_INDEX_ES_QUERY,
    cacheKey: ProductCache.brandsSelectedFilterCacheKey(),
    query: FETCH_PRODUCT_BRANDS_SELECTED_QUERY,
    initialFilters: {
      scope: { eq: FetchCategoriesScopes.FACILITY_MANUFACTURERS },
      id: { in: brandIds }
    },
    options: {
      staleTime,
      keepPreviousData: true,
      withoutPrefetch: true
    }
  });

  useEffect(() => {
    if (!isEqual(brandIds, productsBrandsSelectedFilters.id?.in)) {
      changeProductBrandsSelectedFilters({
        id: { in: brandIds }
      });
    }
  }, [
    changeProductBrandsSelectedFilters,
    brandIds,
    productsBrandsSelectedFilters.id?.in
  ]);

  const handleRemoveFilter = useCallback(() => {
    changeProductsFilters({}, [FetchProductsFilterFields.BRAND_ID]);
  }, [changeProductsFilters]);

  const valueCount = size(brandIds);

  if (!valueCount) {
    return null;
  }

  return (
    <div className="relative bg-blue-500 bg-opacity-20 dark:text-gray-200 inline-flex items-center m-0.5 px-3 py-1.5 rounded-md text-gray-800 text-sm ">
      <div>
        <Translate id={categoriesKeys.brands.plural} />: &nbsp;
        {size(brandIds) === 1
          ? find(productBrandsSelected, (brand) => brand.id === head(brandIds))
              ?.name
          : valueCount}
      </div>

      <PureIconButtonHelper
        className="flex-shrink-0 ml-1 -my-1 -mr-2 h-6 w-6 rounded inline-flex items-center justify-center text-gray-800 dark:text-gray-200 hover:bg-gray-500 hover:bg-opacity-20 hover:text-gray-700 dark:hover:bg-opacity-20 dark:hover:bg-white dark:hover:text-white"
        dataGa={`${FetchProductsFilterFields.BRAND_ID}-clear`}
        icon={IconsEnum.X}
        iconClassName="h-3.5 w-3.5"
        onClick={handleRemoveFilter}
      />
    </div>
  );
}

export default ProductsBrandsFilterBadge;
